import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiDownload,
  FiShare2,
  FiCheckCircle,
  FiAlertTriangle,
  FiInfo,
  FiChevronDown,
  FiChevronUp,
  FiExternalLink,
  FiActivity,
  FiPackage,
} from "react-icons/fi";
import { SiPreact } from "react-icons/si";
import { resetAudit } from "../store/slices/auditSlice";
import BundleAnalysis from "./BundleAnalysis";
import { fetchGeminiAnalysis } from "../store/slices/geminiSlice";

const ReportPage = ({ shared = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reportData } = useSelector((state) => state.audit);
  const [expandedFix, setExpandedFix] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [activeTab, setActiveTab] = useState("performance"); // performance | bundle
  const {
    recommendations,
    loading: geminiLoading,
    error: geminiError,
  } = useSelector((state) => state.gemini);

  // Give a brief moment for Redux state to update before redirecting
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!reportData && !shared) {
        console.log("No report data found, redirecting to home");
        navigate("/");
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [reportData, shared, navigate]);

  // Add this effect to fetch Gemini analysis when report data is available
  useEffect(() => {
    if (reportData && !recommendations.length) {
      dispatch(
        fetchGeminiAnalysis({
          lighthouseResults: reportData,
          bundleAnalysis: reportData.bundleAnalysis,
        })
      );
    }
  }, [reportData, recommendations.length, dispatch]);

  // Show loading state while waiting for data
  if (!reportData) {
    return (
      <div className="min-h-screen bg-gray-10 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-body-01 text-gray-70">Loading report...</p>
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 90) return "text-green-60";
    if (score >= 50) return "text-yellow-30";
    return "text-red-60";
  };

  const getVitalStatus = (score) => {
    switch (score) {
      case "good":
        return {
          color: "text-green-60",
          bg: "bg-green-60",
          icon: FiCheckCircle,
        };
      case "needs-improvement":
        return {
          color: "text-yellow-30",
          bg: "bg-yellow-30",
          icon: FiAlertTriangle,
        };
      default:
        return { color: "text-red-60", bg: "bg-red-60", icon: FiAlertTriangle };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-60";
      case "medium":
        return "bg-yellow-30";
      default:
        return "bg-blue-600";
    }
  };

  const handleDownload = () => {
    const reportJson = JSON.stringify(reportData, null, 2);
    const blob = new Blob([reportJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `react-audit-${new Date().toISOString().split("T")[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const handleBack = () => {
    dispatch(resetAudit());
    navigate("/");
  };

  const toggleFix = (fixId) => {
    setExpandedFix(expandedFix === fixId ? null : fixId);
  };

  return (
    <div className="min-h-screen bg-gray-10">
      {/* Header */}
      <header className="bg-gray-100 border-b border-gray-30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 text-gray-70 hover:text-gray-100 transition-colors"
              >
                <FiArrowLeft className="h-4 w-4" />
                <span className="text-body-01">Back to audit</span>
              </button>
              <div className="bg-blue-600 p-2">
                <SiPreact className="h-5 w-5 text-white" />
              </div>
              <h1 className="text-heading-04 text-gray-100 font-ibm">
                Performance Report
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleShare}
                className="btn-secondary flex items-center space-x-2"
              >
                <FiShare2 className="h-4 w-4" />
                <span>Share</span>
              </button>
              <button
                onClick={handleDownload}
                className="btn-primary flex items-center space-x-2"
              >
                <FiDownload className="h-4 w-4" />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Report Header */}
        <section className="mb-8">
          <div className="bg-white p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-heading-03 text-gray-100 font-ibm mb-2">
                  {reportData.url}
                </h2>
                <p className="text-body-01 text-gray-70">
                  Audited on{" "}
                  {new Date(reportData.timestamp).toLocaleDateString()}
                </p>
              </div>
              <div className="text-right">
                <div className="text-heading-04 font-ibm mb-1">
                  <span className={getScoreColor(reportData.performanceScore)}>
                    {reportData.performanceScore}
                  </span>
                  <span className="text-gray-70">/100</span>
                </div>
                <p className="text-label-01 text-gray-60 uppercase tracking-wide">
                  Performance Score
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Add this after the performance score display */}
        {reportData.auditMetadata && (
          <div className="mt-4 p-3 bg-gray-10 rounded border border-gray-30">
            <h5 className="text-heading-01 text-gray-100 font-ibm mb-2">
              Score Consistency
            </h5>
            <div className="grid grid-cols-2 gap-4 text-body-01">
              <div>
                <span className="text-gray-70">Runs completed:</span>
                <span className="ml-2 text-gray-100 font-medium">
                  {reportData.auditMetadata.successfulRuns}/
                  {reportData.auditMetadata.totalRuns}
                </span>
              </div>
              <div>
                <span className="text-gray-70">Score range:</span>
                <span className="ml-2 text-gray-100 font-medium">
                  {reportData.auditMetadata.scoreVariation.min}-
                  {reportData.auditMetadata.scoreVariation.max}(
                  {reportData.auditMetadata.scoreVariation.range} point spread)
                </span>
              </div>
            </div>
            {reportData.auditMetadata.scoreVariation.standardDeviation > 5 && (
              <div className="mt-2 flex items-center space-x-2 text-body-01 text-yellow-600">
                <FiAlertTriangle className="h-4 w-4" />
                <span>
                  High score variation detected - results may be less reliable
                </span>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <section className="mb-8">
          <div className="border-b border-gray-30">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab("performance")}
                className={`py-4 px-1 border-b-2 font-medium text-body-01 transition-colors ${
                  activeTab === "performance"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-70 hover:text-blue-900"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FiActivity className="h-4 w-4" />
                  <span>Performance Analysis</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab("bundle")}
                className={`py-4 px-1 border-b-2 font-medium text-body-01 transition-colors ${
                  activeTab === "bundle"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-70 hover:text-blue-900"
                }`}
              >
                <div className="flex items-center space-x-2">
                  <FiPackage className="h-4 w-4" />
                  <span>Bundle Analysis</span>
                  {reportData.bundleError && (
                    <FiAlertTriangle className="h-3 w-3 text-yellow-30" />
                  )}
                </div>
              </button>
            </nav>
          </div>
        </section>

        {/* Tab Content */}
        {activeTab === "performance" && (
          <>
            {/* Web Vitals Summary */}
            <section className="mb-8">
              <h3 className="text-heading-03 text-gray-100 mb-6 font-ibm">
                Core Web Vitals
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(reportData.webVitals).map(([key, vital]) => {
                  const status = getVitalStatus(vital.score);
                  const Icon = status.icon;
                  return (
                    <div key={key} className="card">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <Icon className={`h-4 w-4 ${status.color}`} />
                          <span className="text-label-01 text-gray-70 uppercase tracking-wide">
                            {key.toUpperCase()}
                          </span>
                        </div>
                        <div
                          className={`w-2 h-2 rounded-full ${status.bg}`}
                        ></div>
                      </div>
                      <div className="text-heading-02 text-gray-100 font-ibm mb-1">
                        {vital.value}
                        {key === "cls" ? "" : key.includes("t") ? "s" : "ms"}
                      </div>
                      <div className="text-body-01 text-gray-60">
                        Threshold: {vital.threshold}
                        {key === "cls" ? "" : key.includes("t") ? "s" : "ms"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* AI-Powered Recommendations */}
            <section className="mb-8">
              <div className="flex items-center space-x-2 mb-6">
                <h3 className="text-heading-03 text-gray-100 font-ibm">
                  AI-Powered Recommendations
                </h3>
                <div className="bg-blue-600 text-white text-xs px-2 py-1 rounded">
                  Gemini AI
                </div>
              </div>

              {geminiLoading ? (
                <div className="card p-6">
                  <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4">
                      <div className="h-4 bg-gray-30 rounded w-3/4"></div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-30 rounded"></div>
                        <div className="h-4 bg-gray-30 rounded w-5/6"></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : geminiError ? (
                <div className="card p-6">
                  <div className="text-red-60 flex items-center space-x-2">
                    <FiAlertTriangle className="h-5 w-5" />
                    <span>
                      Failed to load AI recommendations: {geminiError}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, index) => (
                    <div key={index} className="card">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-1 h-8 ${
                              rec.impact === "High"
                                ? "bg-red-60"
                                : rec.impact === "Medium"
                                  ? "bg-yellow-30"
                                  : "bg-blue-600"
                            }`}
                          ></div>
                          <div>
                            <h4 className="text-heading-02 text-gray-100 font-ibm mb-1">
                              {rec.title}
                            </h4>
                            <div className="flex items-center space-x-2">
                              <span className="text-body-01 text-gray-70">
                                Impact: {rec.impact}
                              </span>
                              <span className="text-gray-40">•</span>
                              <span className="text-body-01 text-gray-70">
                                Effort: {rec.effort}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-gray-30">
                        <p className="text-body-01 text-gray-70 mb-4">
                          {rec.description}
                        </p>
                        <div className="bg-gray-10 p-4 rounded">
                          <h5 className="text-heading-01 text-gray-100 font-ibm mb-2">
                            Why This Matters
                          </h5>
                          <p className="text-body-01 text-gray-70">{rec.why}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Top 5 Fixes - Update section to handle missing topFixes gracefully */}
            <section className="mb-8">
              <h3 className="text-heading-03 text-gray-100 mb-6 font-ibm">
                Performance Recommendations
              </h3>
              <div className="space-y-4">
                {reportData.topFixes?.length > 0 ? (
                  reportData.topFixes.map((fix) => (
                    <div key={fix.id} className="card">
                      <div
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleFix(fix.id)}
                      >
                        <div className="flex items-center space-x-4">
                          <div
                            className={`w-1 h-8 ${getPriorityColor(fix.priority)}`}
                          ></div>
                          <div>
                            <h4 className="text-heading-02 text-gray-100 font-ibm mb-1">
                              {fix.title}
                            </h4>
                            <p className="text-body-01 text-gray-70">
                              {fix.impact}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`text-label-01 uppercase tracking-wide px-2 py-1 rounded text-white ${getPriorityColor(fix.priority)}`}
                          >
                            {fix.priority}
                          </span>
                          {expandedFix === fix.id ? (
                            <FiChevronUp className="h-5 w-5 text-gray-60" />
                          ) : (
                            <FiChevronDown className="h-5 w-5 text-gray-60" />
                          )}
                        </div>
                      </div>

                      {expandedFix === fix.id && (
                        <div className="mt-4 pt-4 border-t border-gray-30">
                          <div className="mb-4">
                            <h5 className="text-heading-01 text-gray-100 font-ibm mb-2">
                              Description
                            </h5>
                            <p className="text-body-01 text-gray-70 mb-4">
                              {fix.description}
                            </p>
                          </div>

                          <div className="bg-blue-600 p-4 rounded">
                            <h5 className="text-heading-01 text-white font-ibm mb-2 flex items-center space-x-2">
                              <FiInfo className="h-4 w-4" />
                              <span>Recommended Solution</span>
                            </h5>
                            <p className="text-body-01 text-white opacity-90">
                              {fix.solution}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="card">
                    <div className="text-center py-8">
                      <FiCheckCircle className="h-12 w-12 text-green-60 mx-auto mb-4" />
                      <h4 className="text-heading-02 text-gray-100 font-ibm mb-2">
                        Great Performance!
                      </h4>
                      <p className="text-body-01 text-gray-70 mb-4">
                        Your application is performing well. Here are some
                        general optimization tips:
                      </p>
                      <div className="text-left space-y-3">
                        <div className="flex items-start space-x-3">
                          <div className="w-1 h-8 bg-blue-600"></div>
                          <div>
                            <h5 className="text-heading-01 text-gray-100 font-ibm">
                              Monitor Regularly
                            </h5>
                            <p className="text-body-01 text-gray-70">
                              Keep monitoring your performance as your
                              application grows
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-1 h-8 bg-blue-600"></div>
                          <div>
                            <h5 className="text-heading-01 text-gray-100 font-ibm">
                              Image Optimization
                            </h5>
                            <p className="text-body-01 text-gray-70">
                              Ensure images are properly optimized and served in
                              modern formats
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <div className="w-1 h-8 bg-blue-600"></div>
                          <div>
                            <h5 className="text-heading-01 text-gray-100 font-ibm">
                              Code Splitting
                            </h5>
                            <p className="text-body-01 text-gray-70">
                              Implement code splitting to reduce initial bundle
                              size
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {activeTab === "bundle" && (
          <div>
            {/* Debug info - remove this after fixing */}
            {process.env.NODE_ENV === "development" && (
              <div className="bg-yellow-100 border border-yellow-300 p-4 mb-4 rounded">
                <p className="text-sm">
                  <strong>Debug:</strong> bundleAnalysis ={" "}
                  {JSON.stringify(!!reportData.bundleAnalysis)}
                </p>
                <p className="text-sm">
                  <strong>bundleError:</strong>{" "}
                  {reportData.bundleError || "None"}
                </p>
              </div>
            )}
            <BundleAnalysis bundleData={reportData.bundleAnalysis} />
          </div>
        )}

        {/* Additional Actions */}
        <section className="bg-gray-90 p-6 rounded">
          <h3 className="text-heading-03 text-white mb-4 font-ibm">
            Next Steps
          </h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <FiCheckCircle className="h-5 w-5 text-green-60 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-heading-02 text-white font-ibm mb-1">
                  Implement Fixes
                </h4>
                <p className="text-body-01 text-gray-30">
                  Start with high-priority items for maximum impact
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <FiExternalLink className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-heading-02 text-white font-ibm mb-1">
                  Re-audit
                </h4>
                <p className="text-body-01 text-gray-30">
                  Run another audit after implementing changes
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded max-w-md w-full mx-4">
            <h3 className="text-heading-03 text-gray-100 font-ibm mb-4">
              Share Report
            </h3>
            <p className="text-body-01 text-gray-70 mb-4">
              Share this performance report with your team
            </p>
            <div className="flex items-center space-x-2 mb-4">
              <input
                type="text"
                value={`${window.location.origin}/report/${btoa(reportData.url)}`}
                readOnly
                className="flex-1 p-2 border border-gray-30 rounded text-body-01"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.origin}/report/${btoa(reportData.url)}`
                  );
                }}
                className="btn-primary px-4"
              >
                Copy
              </button>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPage;
